/**
 * client/ui/familiars.ts
 * Responsabilidad: Gestor del Modal del Santuario de Familiares Alquímicos (Mascotas).
 * Soporte de filtros por elemento (sin 'Todos'), catálogo dinámico, adopción (compra) autoritativa,
 * equipamiento / desequipamiento en tiempo real con sincronización en Room y previsualizador.
 */

import type { Room } from "@colyseus/sdk";
import Swal from "sweetalert2";
import {
    type FamiliarDef,
    type FamiliarElement,
    FALLBACK_FAMILIARS,
    getFamiliarDef,
    formatFamiliarDescription,
} from "../familiars";
import { questManager } from "../quests";
import { safeGetStorage, safeSetStorage } from "../utils";
import type { MenuManager } from "./menu";

export class FamiliarsModalManager {
    private modalEl: HTMLElement | null = null;
    private overlayEl: HTMLElement | null = null;
    private closeBtn: HTMLButtonElement | null = null;
    private orbsCountEl: HTMLElement | null = null;
    private cardsGridEl: HTMLElement | null = null;
    private unequipBtn: HTMLButtonElement | null = null;
    private filterButtons: NodeListOf<HTMLButtonElement> | null = null;

    private activeElement: FamiliarElement = "water";
    private catalog: FamiliarDef[] = [...FALLBACK_FAMILIARS];
    private unlockedFamiliars: string[] = [];
    private equippedFamiliar: string = "";
    private astralOrbs: number = 0;

    /** Timestamp para evitar colisión de clics de apertura inmediata con el backdrop overlay */
    private openGraceTimestamp: number = 0;

    private menu: MenuManager | null = null;
    private room: Room | null = null;

    constructor() {
        this.initDomReferences();
        this.bindEvents();
        void this.fetchCatalog();
        this.restoreLocalEquipped();

        if (typeof window !== "undefined") {
            window.addEventListener("alchem:auth_change", () => {
                void this.refreshUserData();
            });
        }
    }

    public setMenu(menu: MenuManager): void {
        this.menu = menu;
    }

    public setRoom(room: Room | null): void {
        this.room = room;
    }

    private getAuthToken(): string | null {
        return safeGetStorage("alchem_auth_token") || safeGetStorage("colyseus-auth-token") || null;
    }

    private initDomReferences(): void {
        this.modalEl = document.getElementById("familiars-modal");
        this.overlayEl = document.querySelector(".familiars-modal-overlay");
        this.closeBtn = document.getElementById("familiars-modal-close") as HTMLButtonElement | null;
        this.orbsCountEl = document.getElementById("familiars-orbs-count");
        this.cardsGridEl = document.getElementById("familiars-cards-grid");
        this.unequipBtn = document.getElementById("familiar-unequip-btn") as HTMLButtonElement | null;
        this.filterButtons = document.querySelectorAll<HTMLButtonElement>(".familiar-filter-btn");
    }

    private bindEvents(): void {
        // Cerrar con botón X
        if (this.closeBtn) {
            this.closeBtn.addEventListener("click", () => this.close());
        }

        // Cerrar con clic en el backdrop con protección de gracia de 100ms
        if (this.overlayEl) {
            this.overlayEl.addEventListener("click", () => {
                if (Date.now() - this.openGraceTimestamp < 100) return;
                this.close();
            });
        }

        // Cerrar con tecla Escape
        window.addEventListener("keydown", (e: KeyboardEvent) => {
            if (e.key === "Escape" && this.isOpen()) {
                this.close();
            }
        });

        // Pestañas de filtrado elemental (sin 'Todos')
        if (this.filterButtons) {
            this.filterButtons.forEach((btn) => {
                btn.addEventListener("click", () => {
                    const el = btn.dataset.element as FamiliarElement;
                    if (el && el !== this.activeElement) {
                        this.activeElement = el;
                        this.updateActiveFilterTab();
                        this.renderCards();
                    }
                });
            });
        }

        // Botón Desequipar / Retirar
        if (this.unequipBtn) {
            this.unequipBtn.addEventListener("click", () => {
                void this.handleUnequip();
            });
        }
    }

    private restoreLocalEquipped(): void {
        const stored = safeGetStorage("alchem_familiar");
        if (stored && stored !== "none") {
            this.equippedFamiliar = stored;
        }
        const storedUnlocked = safeGetStorage("alchem_unlocked_familiars");
        if (storedUnlocked) {
            try {
                this.unlockedFamiliars = JSON.parse(storedUnlocked);
            } catch {
                this.unlockedFamiliars = [];
            }
        }
    }

    public isOpen(): boolean {
        return !!this.modalEl && !this.modalEl.classList.contains("hidden");
    }

    /**
     * Abre el modal del Santuario de Familiares.
     */
    public open(): void {
        if (!this.modalEl) return;
        this.openGraceTimestamp = Date.now();
        this.modalEl.classList.remove("hidden");
        document.body.classList.add("modal-open");

        void this.refreshUserData();
        this.updateActiveFilterTab();
        this.renderCards();
    }

    /**
     * Cierra el modal.
     */
    public close(): void {
        if (!this.modalEl) return;
        this.modalEl.classList.add("hidden");
        document.body.classList.remove("modal-open");
    }

    /**
     * Consulta el catálogo autoritativo en /api/familiars/catalog con fallback transparente.
     */
    public async fetchCatalog(): Promise<void> {
        try {
            const res = await fetch("/api/familiars/catalog");
            if (res.ok) {
                const data = await res.json();
                if (data && data.success && Array.isArray(data.catalog) && data.catalog.length > 0) {
                    this.catalog = (data.catalog as Array<Record<string, unknown>>).map((item) => ({
                        id: String(item.id),
                        element: item.element as FamiliarElement,
                        tier: Number(item.tier ?? 1),
                        name: String(item.name || ""),
                        priceOrbs: Number(item.priceOrbs ?? 15),
                        priceCrystals: Number(item.priceCrystals ?? 1500),
                        buffType: String(item.buffType || "speed"),
                        baseValue: Number(item.baseValue ?? 0.03),
                        affinityBonus: Number(item.affinityBonus ?? 0.01),
                        description: String(item.description || ""),
                        assetPath: (item.assetPath as string) || `assets/familiars/${item.id}.png`,
                        rarity: (item.rarity as "rare" | "epic" | "exclusive") || "rare",
                    }));
                }
            }
        } catch (e) {
            console.warn("[FamiliarsModal] Error al obtener catálogo de familiares, usando fallback:", e);
            this.catalog = [...FALLBACK_FAMILIARS];
        }

        if (this.isOpen()) {
            this.renderCards();
        }
    }

    /**
     * Sincroniza familiares desbloqueados, equipado y saldo de orbes con el backend.
     */
    public async refreshUserData(): Promise<void> {
        const token = this.getAuthToken();

        // Saldo local inmediato desde questManager
        this.astralOrbs = questManager.getAstralOrbs();
        this.updateOrbsBalanceDisplay();

        if (this.menu) {
            const currentCosmetics = this.menu.getCosmetics();
            if (currentCosmetics.familiar && currentCosmetics.familiar !== "none") {
                this.equippedFamiliar = currentCosmetics.familiar;
            }
        }

        if (!token) {
            this.renderCards();
            return;
        }

        try {
            const res = await fetch("/api/familiars/status", {
                headers: { Authorization: `Bearer ${token}` },
            });

            if (res.ok) {
                const data = await res.json();
                if (data && data.success) {
                    if (Array.isArray(data.unlockedFamiliars)) {
                        this.unlockedFamiliars = data.unlockedFamiliars;
                        safeSetStorage("alchem_unlocked_familiars", JSON.stringify(this.unlockedFamiliars));
                    }
                    if (typeof data.equippedFamiliar === "string") {
                        this.equippedFamiliar = data.equippedFamiliar;
                        if (this.equippedFamiliar) {
                            safeSetStorage("alchem_familiar", this.equippedFamiliar);
                        }
                    }
                    if (typeof data.astralOrbs === "number") {
                        this.astralOrbs = data.astralOrbs;
                        questManager.updateBalances(questManager.getCrystals(), this.astralOrbs);
                        this.updateOrbsBalanceDisplay();
                    }
                }
            }
        } catch (err) {
            console.warn("[FamiliarsModal] Error al consultar /api/familiars/status:", err);
        }

        this.renderCards();
    }

    private updateOrbsBalanceDisplay(): void {
        if (this.orbsCountEl) {
            this.orbsCountEl.textContent = `🔮 ${this.astralOrbs}`;
        }
        const crystalsEl = document.getElementById("familiars-crystals-count");
        if (crystalsEl) {
            crystalsEl.textContent = `💎 ${questManager.getCrystals()}`;
        }
    }

    private updateActiveFilterTab(): void {
        if (!this.filterButtons) return;
        this.filterButtons.forEach((btn) => {
            if (btn.dataset.element === this.activeElement) {
                btn.classList.add("active");
            } else {
                btn.classList.remove("active");
            }
        });
    }

    /**
     * Renderiza las 3 tarjetas de familiares para el elemento activo.
     */
    public renderCards(): void {
        const grid = this.cardsGridEl;
        if (!grid) return;
        grid.innerHTML = "";

        const familiarsForElement = this.catalog.filter((f) => f.element === this.activeElement);
        // Asegurar orden por Tier
        familiarsForElement.sort((a, b) => a.tier - b.tier);

        familiarsForElement.forEach((fam) => {
            const isEquipped = this.equippedFamiliar === fam.id;
            const isUnlocked = this.unlockedFamiliars.includes(fam.id);

            let rarityClass = "rarity-rare";
            let ribbonText = "✨ RARO";
            let ribbonClass = "ribbon-rare";
            if (fam.tier === 2) {
                rarityClass = "rarity-epic";
                ribbonText = "⚡ ÉPICO";
                ribbonClass = "ribbon-epic";
            } else if (fam.tier === 3) {
                rarityClass = "rarity-exclusive";
                ribbonText = "👑 EXCLUSIVO";
                ribbonClass = "ribbon-exclusive";
            }

            const card = document.createElement("div");
            card.className = `familiar-card ${rarityClass} ${isEquipped ? "is-equipped" : ""}`;

            const tierRoman = fam.tier === 1 ? "Tier I" : fam.tier === 2 ? "Tier II" : "Tier III";

            let actionBtnHtml = "";
            if (isEquipped) {
                actionBtnHtml = `<button type="button" class="familiar-action-btn familiar-btn-equipped" disabled>Equipado ✨</button>`;
            } else if (isUnlocked) {
                actionBtnHtml = `<button type="button" class="familiar-action-btn familiar-btn-equip" data-id="${fam.id}">Equipar</button>`;
            } else {
                actionBtnHtml = `<button type="button" class="familiar-action-btn familiar-btn-buy" data-id="${fam.id}">Desbloquear (🔮 ${fam.priceOrbs} + 💎 ${fam.priceCrystals})</button>`;
            }

            card.innerHTML = `
                <div class="familiar-ribbon ${ribbonClass}">${ribbonText}</div>
                <div class="familiar-tier-badge familiar-tier-${fam.tier}">${tierRoman}</div>
                <div class="familiar-img-wrap">
                    <img src="${fam.assetPath}" alt="${fam.name}" class="familiar-img" loading="lazy">
                </div>
                <div class="familiar-name">${fam.name}</div>
                <div class="familiar-price-dual">
                    <span class="price-orbs">🔮 ${fam.priceOrbs} Orbes</span>
                    <span class="price-plus">+</span>
                    <span class="price-crystals">💎 ${fam.priceCrystals} Cristales</span>
                </div>
                <div class="familiar-buff-desc">${formatFamiliarDescription(fam)}</div>
                ${actionBtnHtml}
            `;

            // Event Listeners para botones de acción
            const equipBtn = card.querySelector(".familiar-btn-equip");
            if (equipBtn) {
                equipBtn.addEventListener("click", () => {
                    void this.handleEquip(fam.id);
                });
            }

            const buyBtn = card.querySelector(".familiar-btn-buy");
            if (buyBtn) {
                buyBtn.addEventListener("click", () => {
                    void this.handleBuy(fam);
                });
            }

            grid.appendChild(card);
        });
    }

    /**
     * Maneja la compra / desbloqueo autoritativo de un familiar con ECONOMÍA DUAL ESTRICTA.
     */
    private async handleBuy(fam: FamiliarDef): Promise<void> {
        const token = this.getAuthToken();

        if (!token) {
            Swal.fire({
                title: "Inicia Sesión",
                text: "Debes iniciar sesión con tu cuenta para desbloquear a esta mascota y guardarla para siempre.",
                icon: "info",
                confirmButtonText: "Entendido",
                confirmButtonColor: "#38bdf8",
            });
            return;
        }

        const userCrystals = questManager.getCrystals();
        if (this.astralOrbs < fam.priceOrbs || userCrystals < fam.priceCrystals) {
            Swal.fire({
                title: "Fondos Insuficientes",
                html: `Necesitas <b>🔮 ${fam.priceOrbs} Orbes</b> y <b>💎 ${fam.priceCrystals} Cristales</b> para desbloquear a <b>${fam.name}</b>.<br><br>Saldo actual:<br>🔮 ${this.astralOrbs} Orbes<br>💎 ${userCrystals} Cristales.<br><small style="color:#94a3b8;">¡Completa misiones o gira la ruleta diaria para ganar más orbes y cristales!</small>`,
                icon: "warning",
                confirmButtonText: "Aceptar",
                confirmButtonColor: "#f59e0b",
            });
            return;
        }

        const confirm = await Swal.fire({
            title: `¿Desbloquear a ${fam.name}?`,
            html: `Se descontarán <b>🔮 ${fam.priceOrbs} Orbes</b> y <b>💎 ${fam.priceCrystals} Cristales</b> de tu reserva.<br><br><b>Poder:</b> ${formatFamiliarDescription(fam)}`,
            imageUrl: fam.assetPath,
            imageWidth: 96,
            imageHeight: 96,
            imageAlt: fam.name,
            showCancelButton: true,
            confirmButtonText: "¡Sí, Desbloquear!",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "#f59e0b",
            cancelButtonColor: "#64748b",
        });

        if (!confirm.isConfirmed) return;

        try {
            const res = await fetch("/api/familiars/buy", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ familiarId: fam.id }),
            });

            const data = await res.json();

            if (!res.ok || !data.success) {
                Swal.fire({
                    title: "Error",
                    text: data.message || "No se pudo completar la adopción.",
                    icon: "error",
                    confirmButtonColor: "#ef4444",
                });
                return;
            }

            // Actualizar estado local
            if (Array.isArray(data.unlockedFamiliars)) {
                this.unlockedFamiliars = data.unlockedFamiliars;
                safeSetStorage("alchem_unlocked_familiars", JSON.stringify(this.unlockedFamiliars));
            } else if (!this.unlockedFamiliars.includes(fam.id)) {
                this.unlockedFamiliars.push(fam.id);
                safeSetStorage("alchem_unlocked_familiars", JSON.stringify(this.unlockedFamiliars));
            }

            if (typeof data.astralOrbs === "number" || typeof data.crystals === "number") {
                if (typeof data.astralOrbs === "number") this.astralOrbs = data.astralOrbs;
                questManager.updateBalances(data.crystals ?? questManager.getCrystals(), this.astralOrbs);
                this.updateOrbsBalanceDisplay();
            }

            // Auto-equipar el nuevo familiar adquirido
            await this.handleEquip(fam.id, false);

            Swal.fire({
                title: "¡Mascota Vinculada!",
                text: data.message || `Has adoptado con éxito a ${fam.name}.`,
                icon: "success",
                confirmButtonText: "¡Genial!",
                confirmButtonColor: "#38bdf8",
            });
        } catch (err) {
            console.error("[FamiliarsModal] Error al comprar familiar:", err);
            Swal.fire({
                title: "Error de Red",
                text: "No se pudo conectar con el servidor alquímico.",
                icon: "error",
                confirmButtonColor: "#ef4444",
            });
        }
    }

    /**
     * Equipa un familiar para el jugador en menú, red y backend.
     */
    public async handleEquip(familiarId: string, showToast: boolean = true): Promise<void> {
        this.equippedFamiliar = familiarId;
        safeSetStorage("alchem_familiar", familiarId);

        // 1. Sincronizar en MenuManager y previsualizador
        if (this.menu) {
            this.menu.setFamiliar(familiarId);
            this.menu.updatePreview();
        }

        // 2. Transmitir por red si la sala de Colyseus está conectada
        if (this.room) {
            try {
                const cosmetics = this.menu ? this.menu.getCosmetics() : { familiar: familiarId };
                this.room.send("set-skin", { skin: JSON.stringify(cosmetics) });
            } catch (err) {
                console.warn("[FamiliarsModal] Error al enviar set-skin con nuevo familiar:", err);
            }
        }

        // 3. Persistir en backend si hay sesión iniciada
        const token = this.getAuthToken();
        if (token) {
            try {
                await fetch("/api/familiars/equip", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ familiarId }),
                });
            } catch (err) {
                console.warn("[FamiliarsModal] Error al persistir familiar equipado en backend:", err);
            }
        }

        this.renderCards();

        if (showToast) {
            const fam = getFamiliarDef(familiarId);
            const name = fam ? fam.name : "Mascota";
            const Toast = Swal.mixin({
                toast: true,
                position: "bottom-end",
                showConfirmButton: false,
                timer: 2000,
                timerProgressBar: true,
                background: "#0f172a",
                color: "#38bdf8",
            });
            Toast.fire({
                icon: "success",
                title: `🐾 ${name} equipada`,
            });
        }
    }

    /**
     * Retira el familiar actualmente equipado.
     */
    public async handleUnequip(): Promise<void> {
        this.equippedFamiliar = "";
        safeSetStorage("alchem_familiar", "none");

        if (this.menu) {
            this.menu.setFamiliar("none");
            this.menu.updatePreview();
        }

        if (this.room) {
            try {
                const cosmetics = this.menu ? this.menu.getCosmetics() : { familiar: "none" };
                this.room.send("set-skin", { skin: JSON.stringify(cosmetics) });
            } catch (err) {
                console.warn("[FamiliarsModal] Error al enviar set-skin tras retirar familiar:", err);
            }
        }

        const token = this.getAuthToken();
        if (token) {
            try {
                await fetch("/api/familiars/equip", {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ familiarId: "none" }),
                });
            } catch (err) {
                console.warn("[FamiliarsModal] Error al retirar familiar en backend:", err);
            }
        }

        this.renderCards();

        const Toast = Swal.mixin({
            toast: true,
            position: "bottom-end",
            showConfirmButton: false,
            timer: 2000,
            timerProgressBar: true,
            background: "#0f172a",
            color: "#94a3b8",
        });
        Toast.fire({
            icon: "info",
            title: "Mascota retirada",
        });
    }
}
